import React, { useEffect, useState } from 'react';
import { CheckCircle2, Mail } from 'lucide-react';
import { useMarketplaceAuth } from './MarketplaceAuthContext';
import MarketplaceShell from './MarketplaceShell';
import { MktButton, Eyebrow } from './marketplaceUi';
import { getSubCategories, getUseCases, mktSignup, mktResendOtp } from './marketplaceApi';

type Mode = 'login' | 'signup' | 'verify';

const inputCls = 'w-full rounded-lg border border-black/20 px-3 py-2.5 text-[14px] outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20';

/**
 * Labelled field. Defined at module scope (NOT inside the component) so React
 * keeps the input mounted across keystrokes. Auto-applies an aria-label from the
 * field label, since the placeholders are example values.
 */
const Field: React.FC<{ children: React.ReactNode; label: string; hint?: string }> = ({ children, label: l, hint }) => (
  <div className="flex flex-col gap-1.5">
    <span className="flex items-center justify-between">
      <span className="text-[12px] text-[#6b6b6b]">{l}</span>
      {hint && <span className="text-[11px] text-[#999]">{hint}</span>}
    </span>
    {React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement, {
          'aria-label': (children as React.ReactElement).props['aria-label'] || l,
        })
      : children}
  </div>
);

/**
 * Coerce any backend error shape into a readable string so we never render an
 * object as a React child (FastAPI 422 returns an array of {msg, loc}).
 */
function toMessage(value: unknown, fallback = 'Something went wrong'): string {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const msgs = value.map((v) => toMessage(v, '')).filter(Boolean);
    return msgs.length ? msgs.join('; ') : fallback;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return toMessage(o.detail ?? o.error ?? o.message ?? o.msg, fallback);
  }
  return String(value);
}

/**
 * Gates the "run a study" flow behind the EXISTING platform auth (business email
 * enforced server-side). Ported from account_signup.html: two-column sign-up
 * with a "what you get / from the study" side panel, a confirmation card, and a
 * compact login. Browsing stays public; only running is gated.
 */
const MarketplaceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, login, verifyOtp } = useMarketplaceAuth();
  const [mode, setMode] = useState<Mode>('signup');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState<{ twins: number; subs: number; archetypes: number; useCases: number } | null>(null);

  // Public catalog stats for the signup side panel (best-effort).
  useEffect(() => {
    Promise.all([getSubCategories(), getUseCases()])
      .then(([subs, ucs]) => setStats({
        twins: subs.reduce((n, s) => n + s.twin_count, 0),
        subs: subs.length,
        archetypes: subs.reduce((n, s) => n + s.archetype_count, 0),
        useCases: ucs.length,
      }))
      .catch(() => { /* panel just hides its numbers */ });
  }, []);

  if (isAuthenticated) return <>{children}</>;

  // Marketplace signup (separate from the platform /signup) — creates an
  // account_type='marketplace' user and sends an OTP.
  const doSignup = async () => {
    const name = `${firstName} ${lastName}`.trim();
    if (name.length < 2) { setError('Please enter your name.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    const { ok, data } = await mktSignup({ name, email, password, company, policies_accepted: true });
    if (ok) { setMode('verify'); setInfo(null); return; }
    setError(toMessage(data?.detail ?? data?.error ?? data?.message, 'Registration failed'));
  };

  const doResend = async () => {
    setError(null); setInfo(null);
    try {
      const { ok, data } = await mktResendOtp(email);
      if (ok) setInfo('A new code is on its way — check your inbox.');
      else setError(toMessage(data, 'Could not resend the code.'));
    } catch (e) {
      setError(toMessage(e, 'Could not resend the code.'));
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        await doSignup();
      } else if (mode === 'login') {
        const res = await login(email, password);
        if (!res.success) {
          if (res.requiresVerification) { setMode('verify'); setInfo('Please verify your email. Enter the code we sent you.'); }
          else setError(toMessage(res.message, 'Log in failed'));
        }
      } else {
        const res = await verifyOtp(email, otp);
        if (!res.success) { setError(toMessage(res.message, 'Verification failed')); return; }
        await login(email, password); // establish session post-verify
      }
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const benefits = [
    ['One complete use case', 'Chat, survey, concept test, or packaging test — your choice'],
    ['One audience segment', 'Pick any archetype across all 6 breakfast sub-categories'],
    ['Real behavioral depth', 'Twins built from 1,000 real human surveys — not synthetic proxies'],
    ['Results in minutes', 'No scheduling, no recruitment, no waiting'],
  ];

  const messages = (
    <>
      {error && <div className="text-[13px] text-red-600">{error}</div>}
      {info && <div className="text-[13px] text-[#374151]">{info}</div>}
    </>
  );

  // ── Sign-up: two-column (form + benefits/stats panel) ──────────────
  if (mode === 'signup') {
    return (
      <MarketplaceShell>
        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-4 rounded-xl border border-black/10 bg-white p-7">
            <div>
              <h1 className="text-[20px] font-medium text-[#111]">Create your free account</h1>
              <p className="mt-1 text-[13px] leading-relaxed text-[#6b6b6b]">Run your first use case against any Twin — free, no credit card needed.</p>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-2.5">
                <Field label="First name"><input className={inputCls} placeholder="Sarah" value={firstName} onChange={(e) => setFirstName(e.target.value)} required /></Field>
                <Field label="Last name"><input className={inputCls} placeholder="Chen" value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
              </div>
              <Field label="Work email"><input className={inputCls} type="email" placeholder="sarah@kelloggs.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
              <Field label="Password"><input className={inputCls} type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
              <Field label="Company" hint="Optional"><input className={inputCls} placeholder="Kellogg's" value={company} onChange={(e) => setCompany(e.target.value)} /></Field>

              {messages}

              <div className="flex flex-col gap-2.5">
                <MktButton type="submit" fullWidth disabled={busy}>{busy ? 'Please wait…' : 'Create free account →'}</MktButton>
                <p className="text-center text-[11px] leading-relaxed text-[#999]">By signing up you agree to our terms of service and privacy policy.</p>
              </div>
            </form>

            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-black/10" />
              <span className="text-[12px] text-[#999]">already have an account?</span>
              <div className="h-px flex-1 bg-black/10" />
            </div>
            <MktButton variant="secondary" fullWidth onClick={() => { setMode('login'); setError(null); }}>Log in</MktButton>
          </div>

          {/* Side panel */}
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-black/10 bg-white p-5">
              <Eyebrow className="mb-3.5">What you get for free</Eyebrow>
              <div className="flex flex-col">
                {benefits.map(([title, desc], i) => (
                  <div key={title} className={`flex items-start gap-2.5 py-2.5 ${i < benefits.length - 1 ? 'border-b border-black/10' : ''}`}>
                    <CheckCircle2 className="mt-0.5 h-[17px] w-[17px] flex-shrink-0 text-emerald-600" />
                    <div>
                      <div className="text-[13px] font-medium text-[#111]">{title}</div>
                      <div className="text-[12px] leading-snug text-[#6b6b6b]">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {stats && (
              <div className="rounded-xl border border-black/10 bg-white p-5">
                <Eyebrow className="mb-2.5">From the study</Eyebrow>
                <div className="flex flex-col gap-1.5">
                  <StatRow label="Participants surveyed" value={stats.twins.toLocaleString()} />
                  <StatRow label="Sub-categories" value={stats.subs} />
                  <StatRow label="Behavioural archetypes" value={stats.archetypes} />
                  <StatRow label="Use cases available" value={stats.useCases} />
                </div>
              </div>
            )}
          </div>
        </div>
      </MarketplaceShell>
    );
  }

  // ── Verify: confirmation card + "what happens next" ────────────────
  if (mode === 'verify') {
    const steps = [
      ['Confirm your email', 'Enter the code we just sent to activate your account'],
      ['Choose your use case', 'Chat, survey, concept test, or packaging — you pick one'],
      ['Pick your audience segment', 'Select a sub-category and archetype to run against'],
      ['Get results', 'Real behavioral responses from your Twin segment, in minutes'],
    ];
    return (
      <MarketplaceShell>
        <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
          <div className="rounded-xl border border-black/10 bg-white p-7">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle2 className="h-[22px] w-[22px] text-emerald-600" />
            </div>
            <h1 className="text-[20px] font-medium text-[#111]">You're in{firstName ? `, ${firstName}` : ''}</h1>
            <p className="mt-1.5 text-[13px] leading-relaxed text-[#6b6b6b]">
              We sent a confirmation code to <span className="font-medium text-[#111]">{email || 'your email'}</span>. Enter it below to activate your account.
            </p>

            <div className="mb-5 mt-5 rounded-xl bg-[#f5f5f3] p-4">
              <Eyebrow className="mb-3">Your account</Eyebrow>
              <div className="flex flex-col gap-2">
                <StatRow label="Company" value={company || '—'} />
                <StatRow label="Free studies remaining" value={<span className="text-emerald-700">1 of 1</span>} />
              </div>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-3">
              <Field label="Confirmation code"><input className={inputCls} placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} required /></Field>
              {messages}
              <MktButton type="submit" fullWidth disabled={busy}>{busy ? 'Please wait…' : 'Confirm & continue →'}</MktButton>
            </form>

            <div className="mt-4 text-[13px] text-[#6b6b6b]">
              Didn’t get the code?{' '}
              <button type="button" className="font-medium text-primary underline" onClick={doResend}>Resend the confirmation</button>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-black/10 bg-white p-5">
              <Eyebrow className="mb-3.5">What happens next</Eyebrow>
              <div className="flex flex-col gap-3.5">
                {steps.map(([title, desc], i) => (
                  <div key={title} className="flex items-start gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#E8FCFA] text-[11px] font-medium text-[#0B7C74]">{i + 1}</div>
                    <div>
                      <div className="text-[13px] font-medium text-[#111]">{title}</div>
                      <div className="text-[12px] leading-snug text-[#6b6b6b]">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-2.5 rounded-xl bg-[#f5f5f3] p-4">
              <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#6b6b6b]" />
              <div>
                <div className="mb-0.5 text-[13px] font-medium text-[#111]">Didn't get the email?</div>
                <div className="text-[12px] leading-snug text-[#6b6b6b]">
                  Check your spam folder, or{' '}
                  <button type="button" className="text-primary underline" onClick={doResend}>resend the confirmation</button>.
                </div>
              </div>
            </div>
          </div>
        </div>
      </MarketplaceShell>
    );
  }

  // ── Login: compact single card ─────────────────────────────────────
  return (
    <MarketplaceShell maxWidth="max-w-[420px]">
      <div className="flex flex-col gap-4 rounded-xl border border-black/10 bg-white p-7">
        <div>
          <h1 className="text-[20px] font-medium text-[#111]">Welcome back</h1>
          <p className="mt-1 text-[13px] leading-relaxed text-[#6b6b6b]">Log in to your BluePill account to continue.</p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Work email"><input className={inputCls} type="email" placeholder="sarah@kelloggs.com" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
          <Field label="Password"><input className={inputCls} type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
          {messages}
          <MktButton type="submit" fullWidth disabled={busy}>{busy ? 'Please wait…' : 'Log in →'}</MktButton>
        </form>
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-black/10" />
          <span className="text-[12px] text-[#999]">don't have an account?</span>
          <div className="h-px flex-1 bg-black/10" />
        </div>
        <MktButton variant="secondary" fullWidth onClick={() => { setMode('signup'); setError(null); }}>Create free account</MktButton>
      </div>
    </MarketplaceShell>
  );
};

const StatRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between text-[13px]">
    <span className="text-[#6b6b6b]">{label}</span>
    <span className="font-medium text-[#111]">{value}</span>
  </div>
);

export default MarketplaceGate;
